import type { Printer, QzPrinterDetails, PrinterRuntimeStatus } from '@features/printer/types';

import { classifyPrinterStatus, type PrinterStatusLevel } from '@features/printer/utils/printer-status';

export type PrinterRow = {
  printer: Printer;
  /** `false` when QZ Tray is reachable but does not know a printer with this OS name */
  installed: boolean | null;
  statusText: string | null;
  level: PrinterStatusLevel;
  isDefault: boolean;
};

export type PrinterOverview = {
  /** Worst state of everything the operator needs to print: shown on the header button */
  level: Exclude<PrinterStatusLevel, 'unknown'>;
  rows: PrinterRow[];
  issues: string[];
};

type Input = {
  isQzReady: boolean;
  isComputerExist: boolean;
  isIPv4: boolean;
  printerList: Printer[];
  qzPrinters: QzPrinterDetails[];
  printerStatuses: Record<string, PrinterRuntimeStatus>;
};

export const buildPrinterOverview = ({
  isQzReady,
  isComputerExist,
  isIPv4,
  printerList,
  qzPrinters,
  printerStatuses,
}: Input): PrinterOverview => {
  const rows: PrinterRow[] = printerList.map((printer) => {
    const match = qzPrinters.find((item) => item.name === printer.originalName);
    const statusText = printerStatuses[printer.originalName]?.status ?? null;
    const installed = isQzReady ? !!match : null;

    return {
      printer,
      installed,
      statusText,
      level: installed === false ? 'error' : classifyPrinterStatus(statusText),
      isDefault: !!match?.default,
    };
  });

  const issues: string[] = [];
  if (!isQzReady) {
    issues.push('QZ Tray est inaccessible');
  }
  if (!isComputerExist) {
    issues.push("Cet ordinateur n'est pas configuré");
  }
  if (!isIPv4) {
    issues.push('IP locale inaccessible');
  }
  rows.forEach((row) => {
    if (row.installed === false) {
      issues.push(`${row.printer.name} introuvable dans QZ Tray`);
    } else if (row.level === 'error' || row.level === 'warning') {
      issues.push(`${row.printer.name} : ${row.statusText}`);
    }
  });

  const hasError = !isQzReady || rows.some((row) => row.level === 'error');
  const hasWarning = !isComputerExist || !isIPv4 || rows.some((row) => row.level === 'warning');

  return { level: hasError ? 'error' : hasWarning ? 'warning' : 'ok', rows, issues };
};
